/**
 * BİLGİLENDİRME SÖZLÜĞÜ
 *
 * Arayüzdeki her ⓘ ikonunun içeriği buradan gelir.
 *
 * NEDEN MERKEZİ: AVM yönetimi yoğun terimli bir alan. "Doluluk maliyeti
 * oranı" veya "eşik" gibi kavramları bilmeyen bir kullanıcı (yeni işe
 * başlayan bir uzman, mağaza müdürü, kiracı) rakamı görür ama ne anlama
 * geldiğini bilmez. Açıklamaları bileşenlerin içine dağıtmak, aynı terimin
 * iki ekranda iki farklı şekilde anlatılmasına yol açar.
 *
 * Yazım kuralı: önce KISA tanım, sonra gerekiyorsa formül, sonra
 * "ne işe yarar / neye dikkat edilmeli". Kullanıcı üç cümlede anlamalı.
 */

export interface HelpEntry {
  /** Başlık — genelde terimin kendisi. */
  title: string;
  /** Kısa tanım. Tek paragraf, en fazla 2-3 cümle. */
  body: string;
  /** Varsa hesaplama formülü. */
  formula?: string;
  /** Sektör kabulü / referans değer. */
  benchmark?: string;
  /** Dikkat edilmesi gereken nokta. */
  caution?: string;
}

export const HELP: Record<string, HelpEntry> = {
  // ===========================================================================
  // CİRO
  // ===========================================================================
  turnover: {
    title: 'Ciro',
    body: 'Kiracının belirli bir dönemde yaptığı satış tutarıdır. AVM yönetiminde ciro hem kira hesabının girdisi hem de kiracı performansının ana ölçüsüdür.',
    caution:
      'Brüt ciro mu net ciro mu esas alınacağı sözleşmede açıkça yazmalıdır. İadeler ve KDV\'nin dahil olup olmaması uyuşmazlıkların yarısının kaynağıdır.',
  },

  grossTurnover: {
    title: 'Brüt Ciro',
    body: 'İadeler ve indirimler düşülmeden önceki toplam satış tutarı. KDV dahil veya hariç olabilir; sözleşme hangisini söylüyorsa o esas alınır.',
  },

  netTurnover: {
    title: 'Net Ciro',
    body: 'Kira hesabına esas alınan tutar. Brüt cirodan iadeler, iptaller ve sözleşmede belirtilen istisnalar düşülerek bulunur.',
    formula: 'Net Ciro = Brüt Ciro − İadeler − KDV (sözleşmeye göre)',
  },

  turnoverDeclaration: {
    title: 'Ciro Beyanı',
    body: 'Kiracının bir dönem için bildirdiği ciro kaydı. Kiracı portalından, Excel yüklemesiyle veya AVM personeli tarafından girilebilir.',
    caution:
      'Beyanlar asla üzerine yazılmaz. Düzeltme yapıldığında yeni bir revizyon oluşur, eskisi "revize edildi" olarak saklanır — uyuşmazlıkta geçmişi kanıtlamak için zorunludur.',
  },

  turnoverChannel: {
    title: 'Satış Kanalı',
    body: 'Cironun hangi kanaldan geldiği: mağaza içi, online, click & collect veya kurumsal satış.',
    caution:
      'Online satışların ciroya dahil edilip edilmeyeceği sözleşmede açıkça belirtilmelidir. Türkiye perakendesinde en hızlı büyüyen uyuşmazlık konusu budur ve geçmiş veriye sonradan eklenemez.',
  },

  // ===========================================================================
  // KİRA MODELİ
  // ===========================================================================
  rentModel: {
    title: 'Kira Modeli',
    body: 'Sözleşmenin kira hesaplama biçimi. Türkiye AVM pratiğinde en yaygın model hibrit yapıdır: asgari (sabit) kira + cironun belirli bir yüzdesi.',
  },

  minGuaranteeRent: {
    title: 'Asgari Kira',
    body: 'Kiracının cirosu ne olursa olsun ödemeyi taahhüt ettiği taban tutar. Mal sahibi için güvence, kiracı için öngörülebilir maliyettir.',
    caution:
      'Asgari kira genellikle TÜFE veya ÜFE ile endekslenir. Endeksleme yapıldığında ciro payı eşiği de otomatik olarak kayar.',
  },

  turnoverRent: {
    title: 'Ciro Kirası',
    body: 'Kiracının cirosunun belirli bir yüzdesi üzerinden hesaplanan kira. Hibrit modelde yalnızca asgari kirayı aşan kısım faturalanır.',
    formula: 'Ödenecek Kira = MAX(Asgari Kira, Ciro × Ciro Oranı)',
  },

  breakpoint: {
    title: 'Eşik (Breakpoint)',
    body: 'Ciro payının devreye girdiği satış düzeyi. Bu tutarın altındaki cirolarda yalnızca asgari kira ödenir.',
    formula: 'Doğal Eşik = Asgari Kira ÷ Ciro Oranı',
    caution:
      'Eşik sistemde saklanmaz, her hesapta yeniden türetilir. Böylece asgari kira endekslendiğinde eşik de otomatik güncellenir. Sözleşmede farklı bir rakam yazılıysa "yapay eşik" olarak kaydedilir.',
  },

  turnoverRate: {
    title: 'Ciro Oranı',
    body: 'Cironun kiraya dönüşen yüzdesi. Kategoriye göre değişir: market ve teknoloji gibi düşük marjlı işlerde daha düşük, yiyecek-içecek ve modada daha yüksektir.',
    benchmark: 'Tipik aralık: Market %1–3 · Teknoloji %3–5 · Moda %6–10 · Yiyecek-İçecek %8–12',
  },

  annualSettlement: {
    title: 'Yıl Sonu Ciro Mutabakatı',
    body: 'Yıl boyunca aylık tahsil edilen tutarların, yıllık kümülatif ciro üzerinden yeniden hesaplanıp farkın düzeltilmesi.',
    caution:
      'Aylık hesapta zayıf bir ay güçlü bir ayla dengelenemez; kümülatif mutabakatta dengelenir. Bu genellikle kiracı lehinedir. Fazla ödemenin iade edilip edilmeyeceği sözleşmedeki iade maddesine bağlıdır.',
  },

  // ===========================================================================
  // PERFORMANS METRİKLERİ
  // ===========================================================================
  occupancyCostRatio: {
    title: 'Doluluk Maliyeti Oranı (OCR)',
    body: 'Kiracının toplam mekân maliyetinin cirosuna oranı. Kiracının o mağazada ayakta kalıp kalamayacağını gösteren en önemli göstergedir.',
    formula: 'OCR = (Kira + Ortak Gider + Pazarlama Payı) ÷ Ciro × 100',
    benchmark: 'Sağlıklı bant %6–8. %5–10 kabul edilebilir. Bölgesel AVM\'lerde %9–16 görülebilir.',
    caution:
      'Oranın YÜKSELMESİ kötüdür: kiracının kira yükü cirosuna göre ağırlaşıyor demektir. %10 üstü kiracılar yenileme görüşmesinde risk taşır.',
  },

  salesDensity: {
    title: 'm² Başına Ciro (Verimlilik)',
    body: 'Kiralanabilir alanın metrekaresi başına düşen aylık ciro. Farklı büyüklükteki mağazaları adil biçimde karşılaştırmanın tek yoludur.',
    formula: 'm² Başına Ciro = Aylık Ciro ÷ Kiralanabilir Alan (m²)',
    caution:
      'Küçük ama yoğun mağazalar (kozmetik, kuyumcu) yüksek, büyük alan kullanan mağazalar (market, mobilya) düşük değer verir. Kıyaslama daima aynı kategori içinde yapılmalıdır.',
  },

  likeForLike: {
    title: 'Benzer Mağaza Büyümesi (LFL)',
    body: 'Yalnızca iki karşılaştırma döneminde de faaliyette olan mağazaların ciro değişimi. Yeni açılan veya kapanan mağazaların yarattığı yanılsamayı ortadan kaldırır.',
    caution:
      'LFL olmadan bakılan büyüme yanıltıcıdır: yeni açılan 10 mağaza toplam ciroyu yükseltir ama mevcut kiracıların performansı düşüyor olabilir.',
  },

  footfall: {
    title: 'Ziyaretçi Sayısı',
    body: 'AVM\'ye giren ziyaretçi adedi. Kapı, saat ve gün bazında ölçülür.',
    caution:
      'Tek başına anlamlı değildir; ciroyla birlikte okunmalıdır. Ziyaretçi artarken ciro artmıyorsa dönüşüm sorunu vardır.',
  },

  conversionRate: {
    title: 'Dönüşüm Oranı',
    body: 'Ziyaretçilerin ne kadarının alışverişe dönüştüğü. Ziyaretçi sayısı ile işlem (fiş) adedinin oranıdır.',
    formula: 'Dönüşüm = İşlem Adedi ÷ Ziyaretçi Sayısı × 100',
  },

  occupancyRate: {
    title: 'Doluluk Oranı',
    body: 'Kiralanabilir alanın ne kadarının kirada olduğu. Boş alan hem gelir kaybı hem de AVM çekiciliğinin göstergesidir.',
    formula: 'Doluluk = Kirada Olan m² ÷ Toplam Kiralanabilir Alan × 100',
    benchmark: 'Sağlıklı AVM\'lerde %92 üstü. %85 altı kritik kabul edilir.',
  },

  gla: {
    title: 'Kiralanabilir Alan (GLA)',
    body: 'Kiracılara kiralanabilen toplam net alan. Ortak alanlar (koridor, tuvalet, teknik hacim) bu alana dahil değildir.',
  },

  declarationCompletionRate: {
    title: 'Beyan Tamamlanma Oranı',
    body: 'Beklenen ciro beyanlarının ne kadarının alındığı. AVM yönetiminin fiilen takip ettiği operasyonel göstergedir.',
    caution:
      'Eksik beyanlar yalnızca raporu değil FATURAYI da bozar: beyan gelmeyen kiracıya ciro kirası hesaplanamaz.',
  },

  // ===========================================================================
  // FİNANS
  // ===========================================================================
  cam: {
    title: 'Ortak Alan Gideri (OAG)',
    body: 'Güvenlik, temizlik, enerji, havalandırma, peyzaj, sigorta ve yönetim hizmeti gibi ortak alan işletme giderlerinin kiracılara dağıtılan payı.',
    formula: 'Kiracı Payı = Toplam OAG × (Kiracı m² ÷ Toplam Kiralanabilir m²)',
    caution:
      'Yıl başında bütçe üzerinden tahsil edilir, yıl sonunda gerçekleşen giderle mutabakat yapılır. Boş ünitelerin payını kimin üstleneceği sözleşme konusudur.',
  },

  accrual: {
    title: 'Tahakkuk',
    body: 'Bir döneme ait kira, ortak gider ve diğer alacakların hesaplanıp kaydedilmesi. Faturalama bu kayıtlar üzerinden yapılır.',
    caution:
      'Kesinleşmiş tahakkuk satırları değiştirilmez. Düzeltme gerektiğinde yeni bir düzeltme satırı oluşturulur — geçmiş raporların tutarlılığı böyle korunur.',
  },

  aging: {
    title: 'Borç Yaşlandırma',
    body: 'Tahsil edilmemiş alacakların vadesi geçen gün sayısına göre gruplanması. Tahsilat riskinin en hızlı okunduğu tablodur.',
  },

  currentAccount: {
    title: 'Cari Hesap Ekstresi',
    body: 'Bir kiracının tüm borç ve alacak hareketlerinin tarih sırasıyla dökümü. Mutabakat görüşmelerinin temel belgesidir.',
  },

  fxComparison: {
    title: 'Karşılaştırma Para Birimi',
    body: 'Döviz cinsinden sözleşmelerde, asgari kira ile ciro payının hangi para biriminde karşılaştırılacağı.',
    caution:
      'Bu seçim faturayı doğrudan değiştirir. USD asgari kira ve TL ciro varken karşılaştırmanın USD\'de mi TL\'de mi yapılacağı sözleşmede yazmalıdır — kur hareketli dönemlerde aradaki fark ciddi tutarlara ulaşır.',
  },

  escalation: {
    title: 'Kira Artışı (Endeksleme)',
    body: 'Kira bedelinin dönemsel olarak artırılması. Türkiye\'de genellikle TÜFE (Tüketici Fiyat Endeksi) veya ÜFE üzerinden yapılır.',
    caution:
      'Endeks verisi sonradan revize edilebilir. Bu durumda geçmiş tahakkuklar yeniden hesaplanır ve fark düzeltme satırı olarak yansıtılır.',
  },

  bankGuarantee: {
    title: 'Teminat Mektubu',
    body: 'Kiracının yükümlülüklerine karşılık bankadan alınan güvence. Depozitoya alternatif veya ek olarak kullanılır.',
    caution:
      'Süresi dolan teminat mektubu güvence sağlamaz. Sistem, bitişten önce uyarı üretir — yenilenmemesi ciddi bir risktir.',
  },

  // ===========================================================================
  // SİSTEM
  // ===========================================================================
  mallScope: {
    title: 'AVM Kapsamı',
    body: 'Hangi AVM\'nin verisine baktığınızı belirler. "Tüm AVM\'ler" seçildiğinde portföy geneli konsolide gösterilir.',
    caution:
      'Ünite, kiracı ve sözleşme gibi AVM\'ye özgü ekranlar portföy modunda çalışmaz; bu ekranlar için bir AVM seçmeniz gerekir.',
  },

  anomalyFlag: {
    title: 'Sapma İşareti',
    body: 'Beyan edilen cironun, kiracının kendi geçmiş ortalamasına göre beklenmedik ölçüde farklı olduğunu gösterir.',
    caution:
      'Bu bir hata değil, dikkat çağrısıdır. Beyanı engellemez; onaycının yüzlerce kayıt arasında hangilerine daha dikkatli bakması gerektiğini söyler.',
  },

  legalMaintenance: {
    title: 'Yasal Zorunlu Bakım',
    body: 'Asansör, yürüyen merdiven, yangın sistemi ve elektrik panoları için periyodik kontrol mevzuatla zorunludur.',
    benchmark:
      'Asansör: aylık bakım + yıllık yasal kontrol · Yangın algılama: 6 ayda bir · Elektrik panosu: 6 ayda bir',
    caution:
      'İhmali idari para cezası doğurur; kaza halinde cezai sorumluluk gündeme gelir. Bu bakımlar ertelenemez ve bütçe gerekçesiyle atlanamaz.',
  },

  ticketSla: {
    title: 'SLA (Müdahale Süresi)',
    body: 'Bir talebe müdahale için taahhüt edilen azami süre. Önceliğe göre değişir.',
    benchmark: 'Acil 4 saat · Yüksek 8 saat · Normal 24 saat · Düşük 72 saat',
    caution:
      'AVM yönetim sözleşmelerinde müdahale süreleri yazılıdır ve aşımı cezai şart doğurabilir. SLA yalnızca bir hedef değil, sözleşmesel bir taahhüttür.',
  },

  auditTrail: {
    title: 'Denetim İzi',
    body: 'Bir hesabın hangi girdilerle, hangi kurallarla ve hangi adımlarla yapıldığının kaydı.',
    caution:
      'Kiracının mali müşaviri bir tutara itiraz ettiğinde cevap bu kayıttan üretilir: hangi ciro beyanı, kaçıncı revizyonu, hangi kur, hangi aritmetik.',
  },
};

/** Sözlükte olmayan anahtar istendiğinde sessizce boş dönmek yerine uyar. */
export function getHelp(key: string): HelpEntry | null {
  return HELP[key] ?? null;
}
